import React, { useState, useEffect, useCallback } from 'react';
import { 
  ChartBarIcon, 
  ArrowTrendingUpIcon, 
  EyeIcon,
  UserGroupIcon,
  StarIcon,
  CurrencyDollarIcon,
  LinkIcon,
  FireIcon,
  UsersIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartTheme, areaSeriesProps } from '../../utils/analytics';
import { FullPageSkeleton } from '../ui/PageSkeleton';
import PageHeader from '../ui/PageHeader';
import StatsCard from '../ui/StatsCard';
import ChartCard from '../ui/ChartCard';

function AnalyticsDashboard({ presetCompanyId = null, hidePageHeader = false }) {
  const { user } = useAuth();
  const { t, formatMessage } = useLanguage();
  const a = t.admin;
  const v = t.vendor;
  const d = t.dashboard;
  const an = t.analytics;
  const days = t.common.daysShort;
  const { isDark } = useTheme();
  const chart = getChartTheme(isDark);
  const [analytics, setAnalytics] = useState(null);
  const [detailedAnalytics, setDetailedAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAdminAnalytics = useCallback(async () => {
    try {
      const config = presetCompanyId
        ? { withCredentials: true, params: { company_id: presetCompanyId } }
        : { withCredentials: true };
      const [overviewResponse, trendsResponse] = await Promise.all([
        axios.get('/api/admin/analytics/overview', config),
        axios.get('/api/analytics/admin-trends', config),
      ]);
      
      setAnalytics(overviewResponse.data);
      setTrends(trendsResponse.data);
    } catch (error) {
      console.error('Error fetching admin analytics:', error);
      throw error;
    }
  }, [presetCompanyId]);

  const fetchVendorAnalytics = useCallback(async () => {
    try {
      const [basicResponse, detailedResponse, trendsResponse] = await Promise.all([
        axios.get('/api/vendor/stats', { withCredentials: true }),
        axios.get('/api/vendor/stats/detailed', { withCredentials: true }),
        axios.get('/api/vendor/trends', { withCredentials: true })
      ]);
      
      setAnalytics(basicResponse.data);
      setDetailedAnalytics(detailedResponse.data);
      setTrends(trendsResponse.data);
    } catch (error) {
      console.error('Error fetching vendor analytics:', error);
      throw error;
    }
  }, []);

  const fetchClientAnalytics = useCallback(async () => {
    try {
      const [statsResponse, trendsResponse] = await Promise.all([
        axios.get('/api/stats', { withCredentials: true }),
        axios.get('/api/analytics/trends', { withCredentials: true })
      ]);
      
      setAnalytics(statsResponse.data);
      setTrends(trendsResponse.data);
    } catch (error) {
      console.error('Error fetching client analytics:', error);
      throw error;
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      if (user?.is_admin) {
        await fetchAdminAnalytics();
      } else if (user?.is_vendor) {
        await fetchVendorAnalytics();
      } else if (user?.is_client) {
        await fetchClientAnalytics();
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error(d.failedToLoadAnalytics);
    } finally {
      setLoading(false);
    }
  }, [user?.is_admin, user?.is_vendor, user?.is_client, fetchAdminAnalytics, fetchVendorAnalytics, fetchClientAnalytics, d.failedToLoadAnalytics]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Render functions for each user type
  const renderAdminAnalytics = () => {
    if (!analytics || !trends) return null;

  const stats = [
    {
        name: a.totalUsers,
        value: analytics.users?.total || 0,
        change: trends.users?.change || 0,
        changeType: trends.users?.changeType || 'neutral',
        icon: UsersIcon,
        gradient: 'from-orange-500 to-amber-500',
      },
    {
        name: a.totalReferrals,
        value: analytics.referrals?.total || 0,
        change: trends.referrals?.change || 0,
        changeType: trends.referrals?.changeType || 'neutral',
        icon: UserGroupIcon,
        gradient: 'from-green-500 to-emerald-500',
      },
    {
        name: a.platformRevenue,
        value: `$${(analytics.revenue?.total || 0).toLocaleString()}`,
        change: trends.revenue?.change || 0,
        changeType: trends.revenue?.changeType || 'neutral',
        icon: CurrencyDollarIcon,
        gradient: 'from-emerald-500 to-teal-500',
      }
    ];

    const dailyData = days.map((day, index) => ({
      day,
      users: trends.users?.data?.[index] || 0,
      referrals: trends.referrals?.data?.[index] || 0,
      revenue: trends.revenue?.data?.[index] || 0
    }));

    const clientStates = analytics.client_states || {};
    const stateData = [
      { name: a.affiliated, value: clientStates.affiliated || 0, color: '#fb923c' },
      { name: a.contacted, value: clientStates.contacted || 0, color: '#f59e0b' },
      { name: a.visited, value: clientStates.visited || 0, color: '#f97316' },
      { name: a.completed, value: clientStates.completed || 0, color: '#22c55e' }
    ];

    return (
      <>
        {/* Stats Grid */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <StatsCard
              key={stat.name}
              title={stat.name}
              value={stat.value}
              change={stat.change}
              changeType={stat.changeType}
              icon={stat.icon}
              gradient={stat.gradient || 'from-orange-500 to-amber-500'}
              animated
            />
          ))}
        </div>

        <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <ChartCard title={an.platformActivity} icon={ArrowTrendingUpIcon} delay={0.05}>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dailyData}>
                <CartesianGrid strokeDasharray="4 6" stroke={chart.grid} vertical={false} />
                <XAxis dataKey="day" stroke={chart.axis} tickLine={false} axisLine={false} />
                <YAxis stroke={chart.axis} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chart.tooltip} />
                <Area {...areaSeriesProps(chart.colors.primary, chart.areaOpacity)} dataKey="users" name={an.chartNewUsers} />
                <Area {...areaSeriesProps(chart.colors.success, chart.areaOpacityStacked)} dataKey="referrals" name={an.chartReferrals} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title={a.clientStateDistribution} icon={TrophyIcon} delay={0.1}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stateData}
                  cx="50%"
                  cy="50%"
                  innerRadius={64}
                  outerRadius={96}
                  paddingAngle={4}
                  dataKey="value"
                  stroke="none"
                >
                  {stateData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={0.88} />
                  ))}
                </Pie>
                <Tooltip contentStyle={chart.tooltip} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {stateData.map((state, index) => (
                <div key={index} className="flex items-center gap-2 rounded-xl bg-secondary-50/50 px-2.5 py-1.5 ">
                  <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: state.color }} />
                  <span className="text-sm text-secondary-600 ">{state.name}</span>
                  <span className="ml-auto text-sm font-semibold text-secondary-900 ">{state.value}</span>
                </div>
              ))}
            </div>
          </ChartCard>
        </div>

        {/* Top Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Top Vendors */}
          <div className="card-premium overflow-hidden">
            <div className="px-6 py-4 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900  flex items-center">
                <StarIcon className="h-5 w-5 text-orange-600 mr-2" />
                {a.topVendors}
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {(analytics.top_performers?.vendors || []).slice(0, 5).map((vendor, index) => (
                  <div key={vendor.id} className="list-row-soft">
                    <div className="flex items-center space-x-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold ${
                        index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-secondary-400' : index === 2 ? 'bg-orange-600' : 'bg-secondary-300'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-secondary-900 ">{vendor.username}</p>
                        <p className="text-sm text-secondary-500 ">{formatMessage(an.clientsCount, { count: vendor.clients_count })}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-success-600">{vendor.referrals_count}</p>
                      <p className="text-xs text-secondary-500 ">{an.referralsShort}</p>
                    </div>
                  </div>
                ))}
                {(!analytics.top_performers?.vendors || analytics.top_performers.vendors.length === 0) && (
                  <p className="text-center text-secondary-500  py-8">{a.noVendorsYet}</p>
                )}
              </div>
            </div>
          </div>

          {/* Top Clients */}
          <div className="card-premium overflow-hidden">
            <div className="px-6 py-4 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900  flex items-center">
                <StarIcon className="h-5 w-5 text-success-600 mr-2" />
                {a.topClients}
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {(analytics.top_performers?.clients || []).slice(0, 5).map((client, index) => (
                  <div key={client.id} className="list-row-soft">
            <div className="flex items-center space-x-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold ${
                        index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-secondary-400' : index === 2 ? 'bg-orange-600' : 'bg-secondary-300'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-secondary-900 ">{client.username}</p>
                        <p className="text-sm text-secondary-500 ">{formatMessage(an.pointsCount, { count: client.reward_points })}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-orange-600">{client.referrals_count}</p>
                      <p className="text-xs text-secondary-500 ">{an.referralsShort}</p>
                    </div>
                  </div>
                ))}
                {(!analytics.top_performers?.clients || analytics.top_performers.clients.length === 0) && (
                  <p className="text-center text-secondary-500  py-8">{a.noActiveClientsYet}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderVendorAnalytics = () => {
    if (!analytics || !trends) return null;

    const stats = [
      {
        name: v.totalClients,
        value: analytics.totalClients || 0,
        change: trends.clients?.change || 0,
        changeType: trends.clients?.changeType || 'neutral',
        icon: UsersIcon,
        gradient: 'from-orange-500 to-amber-500',
      },
      {
        name: v.totalReferrals,
        value: analytics.totalReferrals || 0,
        change: trends.referrals?.change || 0,
        changeType: trends.referrals?.changeType || 'neutral',
        icon: UserGroupIcon,
        gradient: 'from-green-500 to-emerald-500',
      },
      {
        name: v.totalCommission,
        value: `$${(analytics.totalCommission || 0).toLocaleString()}`,
        change: trends.commission?.change || 0,
        changeType: trends.commission?.changeType || 'neutral',
        icon: CurrencyDollarIcon,
        gradient: 'from-emerald-500 to-teal-500',
      },
      {
        name: v.activeClients,
        value: `${(analytics.activeClientPercentage || 0).toFixed(1)}%`,
        change: 0,
        changeType: 'neutral',
        icon: FireIcon,
        gradient: 'from-red-500 to-orange-500',
      }
    ];

    const dailyData = days.map((day, index) => ({
      day,
      clients: trends.clients?.data?.[index] || 0,
      referrals: trends.referrals?.data?.[index] || 0,
      commission: trends.commission?.data?.[index] || 0
    }));

    const clientStates = detailedAnalytics?.client_states || {};
    const stateData = [
      { name: a.affiliated, value: clientStates.affiliated || 0, color: '#fb923c' },
      { name: a.contacted, value: clientStates.contacted || 0, color: '#f59e0b' },
      { name: a.visited, value: clientStates.visited || 0, color: '#f97316' },
      { name: a.completed, value: clientStates.completed || 0, color: '#22c55e' }
    ];

    return (
      <>
        {/* Stats Grid */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
          {stats.map((stat) => (
            <StatsCard
              key={stat.name}
              title={stat.name}
              value={stat.value}
              change={stat.change}
              changeType={stat.changeType}
              icon={stat.icon}
              gradient={stat.gradient || 'from-orange-500 to-amber-500'}
              animated={typeof stat.value === 'number'}
            />
          ))}
        </div>

        <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          <ChartCard title={an.performanceTrends} icon={ArrowTrendingUpIcon}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="4 6" stroke={chart.grid} vertical={false} />
                <XAxis dataKey="day" stroke={chart.axis} tickLine={false} axisLine={false} />
                <YAxis stroke={chart.axis} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chart.tooltip} />
                <Bar dataKey="referrals" fill={chart.colors.primarySoft} radius={[10, 10, 0, 0]} name={an.chartReferrals} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title={an.clientProgressStates} icon={TrophyIcon} delay={0.08}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stateData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {stateData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {stateData.map((state, index) => (
                <div key={index} className="flex items-center gap-2 rounded-xl bg-secondary-50/50 px-2.5 py-1.5 ">
                  <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: state.color }} />
                  <span className="text-sm text-secondary-600 ">{state.name}</span>
                  <span className="ml-auto text-sm font-semibold text-secondary-900 ">{state.value}</span>
                </div>
              ))}
            </div>
          </ChartCard>
        </div>

        {/* Monthly Performance & Top Client */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Monthly Performance */}
          {detailedAnalytics && (
            <div className="card-premium p-6">
              <h3 className="text-lg font-semibold text-secondary-900  mb-6 flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 text-success-600 mr-2" />
                {an.thisMonthPerformance}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-xl">
                  <span className="text-sm font-medium text-secondary-700 ">{v.newClients}</span>
                  <span className="text-2xl font-semibold text-orange-600">
                    {detailedAnalytics.monthly?.new_clients || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-success-50 rounded-xl">
                  <span className="text-sm font-medium text-secondary-700 ">{v.newReferrals}</span>
                  <span className="text-2xl font-semibold text-success-600">
                    {detailedAnalytics.monthly?.referrals || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl">
                  <span className="text-sm font-medium text-secondary-700 ">{v.commissionEarned}</span>
                  <span className="text-2xl font-semibold text-amber-600">
                    ${(detailedAnalytics.monthly?.commission || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Top Client */}
          <div className="card-premium p-6">
            <h3 className="text-lg font-semibold text-secondary-900  mb-6 flex items-center">
              <StarIcon className="h-5 w-5 text-amber-600 mr-2" />
              {an.topPerformingClient}
            </h3>
            {analytics.topClient ? (
              <div className="flex items-center space-x-4 rounded-2xl bg-gradient-to-r from-orange-50 to-amber-50 p-6 max-lg:flex-col max-lg:gap-3 max-lg:space-x-0 max-lg:p-4  ">
                <div className="h-16 w-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                  <TrophyIcon className="h-8 w-8 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-secondary-900 ">{analytics.topClient.username}</p>
                  <p className="text-sm text-secondary-600 ">
                    {formatMessage(an.successfulReferrals, { count: analytics.topClient.referrals_count })}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-secondary-500 ">{an.noClientData}</p>
              </div>
            )}
          </div>
                    </div>
      </>
    );
  };

  const renderClientAnalytics = () => {
    if (!analytics || !trends) return null;

    const stats = [
      {
        name: d.totalClicks,
        value: analytics.totalClicks || 0,
        change: trends.clicks?.change || 0,
        changeType: trends.clicks?.changeType || 'neutral',
        icon: EyeIcon,
        gradient: 'from-orange-500 to-amber-500',
      },
      {
        name: d.conversions,
        value: analytics.totalConversions || 0,
        change: trends.conversions?.change || 0,
        changeType: trends.conversions?.changeType || 'neutral',
        icon: UserGroupIcon,
        gradient: 'from-green-500 to-emerald-500',
      },
      {
        name: d.activeLinks,
        value: analytics.activeLinks || 0,
        change: 0,
        changeType: 'neutral',
        icon: LinkIcon,
        gradient: 'from-amber-500 to-orange-500',
      }
    ];

    const dailyData = days.map((day, index) => ({
      day,
      clicks: trends.clicks?.data?.[index] || 0,
      conversions: trends.conversions?.data?.[index] || 0
    }));

    return (
      <>
        {/* Stats Grid */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {stats.map((stat) => (
            <StatsCard
              key={stat.name}
              title={stat.name}
              value={stat.value}
              change={stat.change}
              changeType={stat.changeType}
              icon={stat.icon}
              gradient={stat.gradient || 'from-orange-500 to-amber-500'}
              animated
            />
          ))}
        </div>

        <ChartCard title={an.clicksConversions} icon={ArrowTrendingUpIcon} className="mb-8">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyData}>
              <CartesianGrid strokeDasharray="4 6" stroke={chart.grid} vertical={false} />
              <XAxis dataKey="day" stroke={chart.axis} tickLine={false} axisLine={false} />
              <YAxis stroke={chart.axis} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={chart.tooltip} />
              <Area {...areaSeriesProps(chart.colors.primarySoft, chart.areaOpacity)} dataKey="clicks" name={an.chartClicks} />
              <Area {...areaSeriesProps(chart.colors.success, chart.areaOpacityStacked)} dataKey="conversions" name={an.chartConversions} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          <div className="card-premium p-6">
            <div className="flex items-center space-x-3 mb-4">
              <LinkIcon className="h-8 w-8 text-orange-500" />
              <h4 className="text-lg font-semibold text-secondary-900 ">{d.activeLinks}</h4>
            </div>
            <p className="text-4xl font-semibold text-orange-600">{analytics.activeLinks || 0}</p>
            <p className="text-sm text-secondary-600  mt-2">{formatMessage(an.outOfTotalLinks, { total: analytics.totalLinks || 0 })}</p>
          </div>

          <div className="card-premium p-6">
            <div className="flex items-center space-x-3 mb-4">
              <UserGroupIcon className="h-8 w-8 text-success-500" />
              <h4 className="text-lg font-semibold text-secondary-900 ">{d.referrals}</h4>
            </div>
            <p className="text-4xl font-semibold text-success-600">{analytics.referralsCount || 0}</p>
            <p className="text-sm text-secondary-600  mt-2">{an.totalSuccessfulReferrals}</p>
          </div>

          <div className="card-premium p-6">
            <div className="flex items-center space-x-3 mb-4">
              <StarIcon className="h-8 w-8 text-amber-500" />
              <h4 className="text-lg font-semibold text-secondary-900 ">{d.rewardPoints}</h4>
            </div>
            <p className="text-4xl font-semibold text-amber-600">{analytics.rewardPoints || 0}</p>
            <p className="text-sm text-secondary-600  mt-2">{an.earnPointsMilestone}</p>
          </div>
        </div>
      </>
    );
  };

  if (loading) {
    return <FullPageSkeleton statCards={hidePageHeader ? 2 : 3} />;
  }

  return (
    <div className="content-flow">
      {!hidePageHeader ? (
        <PageHeader
          eyebrow={an.eyebrow}
          icon={ChartBarIcon}
          title={t.admin.growthAnalytics}
          subtitle={t.admin.advancedAnalytics}
        />
      ) : null}

      {/* Render appropriate analytics based on user type */}
      {user?.is_admin && renderAdminAnalytics()}
      {user?.is_vendor && renderVendorAnalytics()}
      {user?.is_client && renderClientAnalytics()}
    </div>
  );
}

export default AnalyticsDashboard;
